// Public buyer-view order tracker — /order/:orderId
// Reads /api/marketplace/order/:id/public (no auth required) and shows a stepper timeline.
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Clock, Package, Truck, Bike, Home, XCircle, RefreshCw, Copy, Star, MapPin } from 'lucide-react';
import api from '../../utils/api';

const STEP_ICON = {
  pending: Clock,
  confirmed: CheckCircle2,
  packed: Package,
  shipped: Truck,
  out_for_delivery: Bike,
  delivered: Home,
};

export default function OrderTrackPublic() {
  const { orderId } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.get(`/marketplace/order/${orderId}/public`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Order not found');
    } finally { setBusy(false); }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  if (busy && !data) return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">⏳ Loading order…</div>;
  if (err) return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center p-6" data-testid="order-track-error">
      <XCircle size={48} className="text-rose-400" />
      <div className="mt-3 text-lg font-black">Order not found</div>
      <div className="text-sm opacity-70 mt-1">{err}</div>
      <Link to="/marketplace" className="mt-6 px-4 py-2 rounded-lg bg-purple-500 text-white font-black text-sm">← Back to Marketplace</Link>
    </div>
  );

  const order = data?.order || {};
  const steps = data?.steps || ["pending", "confirmed", "packed", "shipped", "out_for_delivery", "delivered"];
  const labels = data?.labels || {};
  const rejected = order.status === 'rejected';
  const currentIdx = rejected ? -1 : Math.max(0, steps.indexOf(order.status));
  const tracking = order.tracking_updates || [];
  const items = order.items || [];

  const copyLink = () => {
    try { navigator.clipboard.writeText(window.location.href); toast.success('Link copied — share with buyer'); } catch {}
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-950 text-white pb-24" data-testid="order-track-page">
      <div className="max-w-2xl mx-auto p-3 sm:p-4">
        {/* Header */}
        <div className="rounded-2xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg,#1E1B4B,#4338CA,#7C3AED)' }}>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider opacity-80">Order tracking</div>
                <div className="text-lg font-black">#{String(order.id || '').slice(0, 8)}</div>
                <div className="text-[11px] opacity-80 mt-0.5">
                  {new Date(order.created_at || Date.now()).toLocaleString('en-IN')}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <button onClick={copyLink} data-testid="order-copy-link"
                        className="px-3 py-1.5 rounded-full bg-white/20 hover:bg-white/30 text-xs font-black flex items-center gap-1">
                  <Copy size={12} /> Share link
                </button>
                <button onClick={load} data-testid="order-refresh"
                        className="px-3 py-1.5 rounded-full bg-emerald-500/80 text-xs font-black flex items-center gap-1">
                  <RefreshCw size={12} /> Refresh
                </button>
              </div>
            </div>
            <div className="mt-3 text-xs opacity-90">
              <b>{order.buyer_name || 'Buyer'}</b> · {order.buyer_city || '—'}
            </div>
            <div className="text-[11px] opacity-80">Total: <b className="text-yellow-300">{Number(order.total_at || 0).toFixed(0)} AT</b></div>
          </div>
        </div>

        {/* Stepper */}
        <div className="mt-4 p-4 rounded-2xl bg-slate-900/70 border border-slate-800" data-testid="order-track-stepper">
          {rejected ? (
            <div className="flex items-center gap-3 text-rose-300">
              <XCircle size={28} /> <div><div className="font-black">Order rejected</div><div className="text-xs opacity-80">AT amount refund kar diya gaya hai.</div></div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-1 relative overflow-x-auto">
              {steps.map((s, i) => {
                const Icon = STEP_ICON[s] || Clock;
                const done = i <= currentIdx;
                const active = i === currentIdx;
                return (
                  <div key={s} className="flex-1 flex flex-col items-center min-w-[54px] relative" data-testid={`step-${s}`}>
                    {i > 0 && (
                      <div className={`absolute top-4 -left-1/2 w-full h-0.5 ${i <= currentIdx ? 'bg-emerald-400' : 'bg-slate-700'}`} />
                    )}
                    <div className={`relative z-10 w-9 h-9 rounded-full flex items-center justify-center border-2 ${
                      done ? 'bg-emerald-500 border-emerald-300 text-white' : 'bg-slate-800 border-slate-700 text-slate-500'
                    } ${active ? 'ring-4 ring-emerald-400/40 animate-pulse' : ''}`}>
                      <Icon size={16} />
                    </div>
                    <div className={`text-[10px] font-black mt-1.5 text-center whitespace-nowrap ${done ? 'text-emerald-200' : 'text-slate-500'}`}>
                      {labels[s] || s}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Timeline (audit trail) */}
        {tracking.length > 0 && (
          <div className="mt-4 p-4 rounded-2xl bg-slate-900/50 border border-slate-800" data-testid="order-track-timeline">
            <div className="text-[11px] font-black opacity-80 mb-2">📋 Timeline</div>
            <div className="space-y-2">
              {[...tracking].reverse().map((t, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <div className="mt-1 w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                  <div className="flex-1">
                    <div className="font-black">{labels[t.status] || t.status}</div>
                    <div className="opacity-70 text-[11px]">{t.text}</div>
                    <div className="opacity-50 text-[10px]">{new Date(t.at).toLocaleString('en-IN')}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Items */}
        {items.length > 0 && (
          <div className="mt-4 p-4 rounded-2xl bg-slate-900/50 border border-slate-800" data-testid="order-track-items">
            <div className="text-[11px] font-black opacity-80 mb-2">📦 Items ({items.length})</div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="flex items-center gap-3 text-xs">
                  {it.image && <img src={it.image} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                  <div className="flex-1">
                    <div className="font-black">{it.title}</div>
                    <div className="opacity-70 text-[11px]">{it.shop_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="opacity-80 text-[11px]">×{it.quantity}</div>
                    <div className="text-yellow-300 font-black">{Number(it.line_total_at).toFixed(0)} AT</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Live GPS map — only while delivery in-flight */}
        {order.delivery_partner_gps && ['accepted', 'picked_up', 'out_for_delivery'].includes(
          (order.tracking_updates || []).slice(-1)[0]?.status || order.status) && (
          <PartnerLiveMap gps={order.delivery_partner_gps}
                          partnerName={order.delivery_partner_name}
                          partnerProfile={order.delivery_partner_profile}
                          buyerCity={order.buyer_city} />
        )}

        {/* Rate the partner (only after delivered, only if partner assigned & not rated yet) */}
        {['delivered', 'completed'].includes(order.status) && order.delivery_partner_id && !order.partner_rating && (
          <RatePartnerWidget orderId={order.id} partnerName={order.delivery_partner_name} onDone={load} />
        )}
        {order.partner_rating && (
          <div className="mt-4 p-3 rounded-2xl bg-emerald-500/10 border border-emerald-400/50 text-xs" data-testid="order-rating-done">
            <div className="font-black text-emerald-300 mb-1">⭐ You rated your partner:</div>
            <div className="flex items-center gap-0.5">
              {[1,2,3,4,5].map((n) => (
                <Star key={n} size={16} className={n <= order.partner_rating.stars ? 'text-yellow-300 fill-yellow-300' : 'text-slate-600'} />
              ))}
              <span className="ml-2 opacity-80">{order.partner_rating.comment}</span>
            </div>
          </div>
        )}

        <div className="mt-4 text-center">
          <Link to="/marketplace" className="text-xs underline opacity-70" data-testid="order-back-link">← Back to Marketplace</Link>
        </div>
      </div>
    </div>
  );
}


// ============ Live GPS map — uses OpenStreetMap iframe (FREE · no API key · 0-cost) ============
function PartnerLiveMap({ gps, partnerName, partnerProfile, buyerCity }) {
  if (!gps || gps.lat == null || gps.lng == null) return null;
  const ageSec = Math.max(0, Math.floor((Date.now() - new Date(gps.at).getTime()) / 1000));
  const stale = ageSec > 120; // 2 minutes without a beacon
  // OpenStreetMap embed — free tiles, no API key
  const delta = 0.008;
  const bbox = `${gps.lng - delta}%2C${gps.lat - delta}%2C${gps.lng + delta}%2C${gps.lat + delta}`;
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${gps.lat}%2C${gps.lng}`;
  const gmaps = `https://www.google.com/maps/dir/?api=1&destination=${gps.lat},${gps.lng}`;
  return (
    <div className="mt-4 p-3 rounded-2xl bg-gradient-to-br from-indigo-900/70 to-slate-900 border border-cyan-400/50" data-testid="order-partner-live-map">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <div className="text-xs font-black text-cyan-300 flex items-center gap-2">
          <span className="relative">
            <MapPin size={14} />
            <span className={`absolute -top-1 -right-1 w-2 h-2 rounded-full ${stale ? 'bg-amber-400' : 'bg-emerald-400 animate-ping'}`} />
          </span>
          Live Location · {partnerName || 'Partner'}
        </div>
        <div className="text-[10px] opacity-80">
          {stale ? `⚠️ Last seen ${ageSec}s ago` : `📡 Updated ${ageSec}s ago`}
        </div>
      </div>
      {partnerProfile && (
        <div className="flex items-center gap-2 text-[10px] mb-2">
          <span className="uppercase font-black opacity-70">{partnerProfile.vehicle_type}</span>
          {partnerProfile.rating_avg > 0 && (
            <span className="flex items-center gap-0.5 text-yellow-300">
              <Star size={10} className="fill-yellow-300" /> {partnerProfile.rating_avg} ({partnerProfile.rating_count})
            </span>
          )}
          <span className="opacity-70">· {partnerProfile.total_deliveries || 0} deliveries</span>
        </div>
      )}
      <div className="rounded-lg overflow-hidden border border-slate-700" style={{ height: 260 }}>
        <iframe title="Partner location" src={src} width="100%" height="100%" style={{ border: 0 }} loading="lazy" data-testid="partner-map-iframe" />
      </div>
      <div className="flex items-center justify-between mt-2 text-[11px]">
        <span className="opacity-80">📍 {gps.lat.toFixed(5)}, {gps.lng.toFixed(5)}
          {gps.speed_kmh > 0 && <> · <b>{gps.speed_kmh.toFixed(0)}</b> km/h</>}
        </span>
        <a href={gmaps} target="_blank" rel="noopener noreferrer" data-testid="partner-directions-link"
           className="px-2 py-1 rounded bg-cyan-500 text-slate-900 font-black text-[10px]">🧭 Directions</a>
      </div>
    </div>
  );
}


// ============ Rate Partner Widget ============
function RatePartnerWidget({ orderId, partnerName, onDone }) {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (stars < 1) { toast.error('Star select karo'); return; }
    setBusy(true);
    try {
      await api.post(`/marketplace/order/${orderId}/rate-partner`, { stars, comment: comment || null });
      toast.success('🙏 Thanks — rating submitted');
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 p-4 rounded-2xl bg-gradient-to-br from-yellow-900/40 to-slate-900 border border-yellow-400/50" data-testid="order-rate-partner">
      <div className="text-sm font-black text-yellow-300 mb-1">⭐ Rate {partnerName || 'your delivery partner'}</div>
      <div className="text-[10px] opacity-80 mb-2">Aapka feedback platform ki quality banata hai</div>
      <div className="flex items-center gap-1 mb-2" onMouseLeave={() => setHover(0)}>
        {[1,2,3,4,5].map((n) => (
          <button key={n} onClick={() => setStars(n)} onMouseEnter={() => setHover(n)}
                  data-testid={`rate-star-${n}`}
                  className="p-1 transition-transform hover:scale-125">
            <Star size={28} className={(hover || stars) >= n ? 'text-yellow-300 fill-yellow-300' : 'text-slate-600'} />
          </button>
        ))}
        {stars > 0 && (
          <span className="ml-2 text-xs font-black text-yellow-300">
            {['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'][stars]}
          </span>
        )}
      </div>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
                placeholder="Comment (optional) — e.g., 'Very fast delivery'"
                data-testid="rate-comment"
                className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
      <button onClick={submit} disabled={busy || stars < 1} data-testid="rate-submit"
              className="mt-2 w-full py-2 rounded-lg bg-yellow-400 text-slate-900 font-black text-xs disabled:opacity-40">
        {busy ? '⏳ Submitting…' : `Submit ${stars > 0 ? `${stars}-star` : ''} Rating`}
      </button>
    </div>
  );
}
